import {
  Container,
  Card,
  CardContent,
  Box,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { SelectProperty } from "../../components/property/selectProperty";
import { useNavigate, useParams } from "react-router-dom";
import { AppCtx } from "../../store/store";
import PropertyModel from "../../models/property/property";
import CustomListItemModel from "../../models/shared/customListItem";
import { showSnackBar } from "../../functions/snackbar";
import { HighlightOff } from "@mui/icons-material";
import { CustomListItem } from "../../components/shared/customListItem";
import { TenanacyContract } from "../../models/property/tenancy";
import {
  acceptRejectTenanacyEndNotice,
  getTenancyEndNotice,
} from "../../apis/property/tenancy";
import { useRejectionNote } from "../../hooks/useRejectionNoteHook";
import { convertDate, formatDateToDDMMYYYY } from "../../functions/modifyText";

export const ViewTenancyEndNotice: React.FC = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const { setIsLoading, propertiesList, getLoggedUser } = useContext(AppCtx);
  const loggedUser = getLoggedUser();
  const [selectedProperty, setSelectedProperty] = useState<PropertyModel>();
  const [tenancyContract, SetTenancyContract] = useState<TenanacyContract>();

  const {
    open,
    note: rejectionNote,
    setNote,
    openDialog,
    closeDialog,
  } = useRejectionNote();

  const handleReject = () => {
    openDialog();
  };

  const handleTenancyEndNoticeResponse = async (
    isAccepted: boolean,
    rejectionNote?: string
  ) => {
    setIsLoading(true);
    var tempSelectedProperty = propertiesList.find(
      (x) => x.id.toString() === propertyId
    )!;

    await acceptRejectTenanacyEndNotice(
      {
        accept: isAccepted,
        rejectionNote: isAccepted ? null : rejectionNote ?? null,
        propertyAddress: tempSelectedProperty.address!,
        contractId: tenancyContract?.contractId!,
        propertyId: propertyId!,
      },
      loggedUser?.token!
    );

    if (!isAccepted) closeDialog();

    setIsLoading(false);
    showSnackBar(
      "success",
      `Tenancy End Notice ${isAccepted ? "Accepted" : "Rejected"}.`
    );
    navigate("/properties/" + propertyId!);
  };

  const handleAccept = () => {
    handleTenancyEndNoticeResponse(true);
  };

  const handleRejectionSubmit = () => {
    handleTenancyEndNoticeResponse(false, rejectionNote);
  };

  useEffect(() => {
    if (propertyId && loggedUser) {
      fetchTenancyEndNotice();
    }
  }, [propertyId]);

  useEffect(() => {
    selectedProperty &&
      navigate(`/properties/view-tenancy-end-notice/${selectedProperty.id}`);
  }, [selectedProperty]);

  async function fetchTenancyEndNotice() {
    await getTenancyEndNotice(propertyId!, loggedUser?.token!)
      .then((response: any) => {
        if (response.status) {
          let tempMaintenanceReports =
            response.data as unknown as TenanacyContract;
          SetTenancyContract(tempMaintenanceReports);
        } else {
          showSnackBar("error", response.message);
        }
      })
      .catch((error) => showSnackBar("error", error));
  }

  const customListItems: CustomListItemModel[] = !tenancyContract
    ? []
    : [
        {
          title: "Notice End Date",
          value: (() => {
            const date = new Date(tenancyContract.noticeEndDate);
            return tenancyContract.noticeEndDate && date.getFullYear() !== 1900
              ? formatDateToDDMMYYYY(date)
              : "Not Available";
          })(),
        },
        {
          title: "Tenancy End Reason",
          value: tenancyContract.tenancyEndReason || "Not Available",
        },
        {
          title: "Rent Due Date Days",
          value: tenancyContract.rentDueDay?.toString() ?? "Not Available",
        },
        {
          title: "Contract ID",
          value: tenancyContract.contractId?.toString() ?? "Not Available",
        },
        {
          title: "Move In Date",
          value: tenancyContract.moveInDate
            ? formatDateToDDMMYYYY(new Date(tenancyContract.moveInDate))
            : "Not Available",
        },
        {
          title: "Move Out Date",
          value: tenancyContract.moveOutDate
            ? formatDateToDDMMYYYY(new Date(tenancyContract.moveOutDate))
            : "Not Available",
        },
        {
          title: "Tenancy Start Date",
          value: tenancyContract.tenancyStartDate
            ? formatDateToDDMMYYYY(new Date(tenancyContract.tenancyStartDate))
            : "Not Available",
        },
        {
          title: "Contract Status",
          value: tenancyContract.contractStatus || "Not Available",
        },
      ];

  return (
    <Container maxWidth="sm">
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <SelectProperty
            selectedPropertyId={propertyId!}
            getSelected={setSelectedProperty}
          />
          {tenancyContract ? (
            <Box mt={2}>
              {customListItems.map((item, index) => (
                <CustomListItem key={index} index={index} item={item} />
              ))}

              <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleAccept}
                >
                  Accept
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleReject}
                >
                  Reject
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box height={"50vh"}>
              <Typography mt={2} textAlign={"center"} color={"red"}>
                No Tenancy End Notice found!
              </Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="center">
            <Tooltip title={"Back to property page"}>
              <IconButton
                color="primary"
                onClick={() => navigate("/properties/" + propertyId!)}
              >
                <HighlightOff sx={{ fontSize: 60 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={open} onClose={closeDialog}>
        <DialogTitle>Enter Rejection Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Rejection Note"
            value={rejectionNote}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleRejectionSubmit} color="error">
            Submit Rejection
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
